import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Experience } from 'src/app/services/experience.service';
import { ExperienceFaqService } from 'src/app/services/experience-faq.service';
import { MainService } from 'src/app/main.service';
import { listingFilterSources } from '../listing-filter.resolver';

function resolveSlug(route: ActivatedRouteSnapshot): string | null {
  let current: ActivatedRouteSnapshot | null = route;
  while (current) {
    const slug = current.paramMap.get('slug');
    if (slug) {
      return slug;
    }
    current = current.parent;
  }
  return null;
}

function resolvePage(route: ActivatedRouteSnapshot): number {
  const pageParam = route.paramMap.get('page');
  if (pageParam?.startsWith('page-')) {
    const parsed = parseInt(pageParam.replace('page-', ''), 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return 1;
}

/**
 * Resolve category detail + first page of experiences + FAQs before init so SSR HTML
 * includes listing content, meta, and crawlable FAQ answers.
 */
export const highLevelCategoryResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot) => {
  const experienceService = inject(Experience);
  const faqService = inject(ExperienceFaqService);
  const mainService = inject(MainService);
  const filters = listingFilterSources(mainService, 'higher-level-category');

  const slug = resolveSlug(route);
  if (!slug) {
    return of(null);
  }

  const state = route.paramMap.get('state') || '';
  const country = route.paramMap.get('country') || '';
  const location_slug = state || country || '';
  const page = resolvePage(route);

  const categoryBody = {
    slug,
    location_slug,
    type: 'higher-level-category',
  };

  return experienceService.getcategoryDetail(categoryBody).pipe(
    switchMap((categoryRes) => {
      if (categoryRes?.response !== 'success') {
        return of({ categoryRes, listingRes: null, faqs: null, page, slug, location_slug });
      }

      const categoryId = categoryRes?.data?.id;
      const listingBody = {
        booking_date: '',
        booking_people: '',
        location_slug,
        price_option: [] as any[],
        category_slug: slug,
        category_type: 'higher-level-category',
        page,
        is_booking: 0,
        search_id: '',
        type: '',
        at_home: false,
        title: '',
        order_by: '',
        order: '',
      };

      return forkJoin({
        categoryRes: of(categoryRes),
        listingRes: experienceService.getExperienceByCategoryPagination(listingBody, '').pipe(
          catchError((err) => {
            console.error('Error resolving high-level category listing:', err);
            return of(null);
          })
        ),
        faqs: categoryId
          ? faqService.listByEntity({ category_id: categoryId }).pipe(
              catchError((err) => {
                console.error('Error resolving high-level category FAQs:', err);
                return of(null);
              })
            )
          : of(null),
        ...filters,
      }).pipe(map((result) => ({ ...result, page, slug, location_slug })));
    }),
    catchError((err) => {
      console.error('Error resolving high-level category listing SEO:', err);
      return of(null);
    })
  );
};
